import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { blogService, BlogPost } from "@/lib/supabase";
import { Button } from "./ui/button";
import { getBlogMeta } from "@/lib/utils";

const BlogSection = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const data = await blogService.getLatestPosts(3);
        setPosts(data);
      } catch (error) {
        console.error("Erro ao buscar posts do blog:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPosts();
  }, []);

  if (!isLoading && posts.length === 0) return null;

  return (
    <section className="py-24 bg-white relative overflow-hidden">
      {/* Background Decor */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-5">
        <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] bg-gold/30 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/20 rounded-full blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold/10 border border-gold/20 text-gold text-xs font-bold uppercase tracking-[0.3em] mb-4"
            >
              Atualizações
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight"
            >
              Diário de <span className="text-gold">Bordo</span>
            </motion.h2>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <Link to="/blog">
              <Button variant="outline" className="border-gold/30 text-gold hover:bg-gold hover:text-white transition-all duration-300 rounded-full px-8">
                Ver todos os artigos
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {isLoading ? (
            [1, 2, 3].map((i) => (
              <div key={i} className="bg-slate-100 rounded-2xl border border-slate-200 overflow-hidden h-[480px] animate-pulse">
                <div className="h-1/2 bg-slate-200" />
                <div className="p-6 space-y-4">
                  <div className="h-4 bg-slate-200 rounded w-1/3" />
                  <div className="h-8 bg-slate-200 rounded w-full" />
                  <div className="h-4 bg-slate-200 rounded w-full" />
                </div>
              </div>
            ))
          ) : (
            posts.map((post, index) => {
              const { title, excerpt } = getBlogMeta(post);
              return (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/blog/${post.slug || post.id}`}
                    className="group relative block h-[480px] w-full overflow-hidden rounded-3xl bg-primary border border-primary/10 shadow-xl transition-all duration-300 hover:shadow-2xl hover:shadow-primary/20"
                  >
                    {/* Image Background */}
                    <div className="absolute inset-0 w-full h-1/2 overflow-hidden bg-slate-800">
                      {post.image_url ? (
                        <img
                          src={post.image_url}
                          alt={title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900 border-b border-white/5">
                          <span className="text-slate-600 text-sm font-medium tracking-widest uppercase">Postagem</span>
                        </div>
                      )}
                    </div>

                    {/* Content Container */}
                    <div className="absolute top-1/2 bottom-0 left-0 right-0 p-8 flex flex-col justify-between bg-primary z-10 border-t border-white/5 transition-colors duration-300 group-hover:bg-[#0f172a]">
                      <div>
                        {/* Date */}
                        <div className="flex items-center gap-2 text-gold/90 mb-4">
                          <Calendar className="w-3.5 h-3.5" />
                          <span className="text-[10px] font-bold uppercase tracking-widest">
                            {format(new Date(post.published_at), "dd 'de' MMM, yyyy", { locale: ptBR })}
                          </span>
                        </div>

                        {/* Title */}
                        <h3 className="text-xl font-bold text-white mb-3 leading-tight line-clamp-2 group-hover:text-gold transition-colors duration-300">
                          {title}
                        </h3>

                        {/* Excerpt */}
                        <p className="text-white/60 text-sm line-clamp-2 mt-2 leading-relaxed">
                           {excerpt}
                        </p>
                      </div>

                      {/* Footer Actions */}
                      <div className="flex items-center justify-between mt-6">
                        {post.hashtags && post.hashtags.length > 0 ? (
                           <div className="flex flex-wrap gap-2">
                              {post.hashtags.slice(0, 2).map((tag, i) => (
                                 <span key={i} className="text-[10px] text-white/40 bg-white/5 px-2 py-1 rounded-md uppercase tracking-wider font-bold">
                                   #{tag}
                                 </span>
                              ))}
                           </div>
                        ) : <div />}

                        <div className="w-8 h-8 rounded-full bg-gold/10 flex items-center justify-center text-gold opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-4 group-hover:translate-x-0">
                          <ArrowRight className="w-4 h-4" />
                        </div>
                      </div>

                      {/* Animated Gold Border Bottom */}
                      <div className="absolute bottom-0 left-0 w-full h-1 bg-gold scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />     
                    </div>
                  </Link>
                </motion.div>
              );
            })
          )}
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
